import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, Send, User, Users, X, Check } from 'lucide-react';

/**
 * WhatsApp-style forward picker — multi-select chats/groups, then send the
 * same message to all of them at once.
 */
const ForwardModal = ({ conversations, onClose, onForward, c, t, isRTL, theme, getAvatarColor, employeeProfilePics }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);

  const candidates = conversations.filter((conv) => {
    if (conv.isArchived) return false;
    const name = [conv.name, conv.arabicName, conv.englishName].filter(Boolean).join(' ').toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const toggle = (conv) => {
    setSelected((prev) =>
      prev.some((x) => String(x.id) === String(conv.id))
        ? prev.filter((x) => String(x.id) !== String(conv.id))
        : [...prev, conv]
    );
  };

  const handleSend = async () => {
    if (!selected.length || sending) return;
    setSending(true);
    try {
      await onForward(selected);
      onClose();
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div
      onClick={onClose}
      data-modal-root="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: theme === 'dark' ? 'rgba(15, 23, 42, 0.7)' : 'rgba(15, 23, 42, 0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px', backdropFilter: 'blur(4px)',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480,
          background: theme === 'dark' ? '#1e293b' : '#ffffff',
          borderRadius: '24px', boxShadow: c.shadow, padding: '24px',
          height: '72vh', border: `1px solid ${c.border}`,
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: c.text }}>{t.forwardTo}</h3>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', color: c.textSecondary, cursor: 'pointer', display: 'flex' }}
            aria-label={t.cancel}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ marginBottom: '14px', position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: isRTL ? 'auto' : '14px', right: isRTL ? '14px' : 'auto', top: '50%', transform: 'translateY(-50%)', color: c.textSecondary }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.searchPeople}
            style={{
              width: '100%',
              padding: `12px ${isRTL ? '40px' : '14px'} 12px ${isRTL ? '14px' : '40px'}`,
              borderRadius: '14px', border: `1px solid ${c.border}`,
              background: c.inputBg, color: c.text, outline: 'none',
            }}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {candidates.map((conv) => {
            const isGroup = conv.isGroup || String(conv.id).startsWith('group-');
            const picked = selected.some((x) => String(x.id) === String(conv.id));
            const avatarSrc = employeeProfilePics?.[conv.id];
            return (
              <div
                key={conv.id}
                className={`employee-item-card ${picked ? 'selected' : ''}`}
                onClick={() => toggle(conv)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '14px', cursor: 'pointer' }}
              >
                <div style={{
                  width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: avatarSrc ? 'transparent' : getAvatarColor(conv.name),
                  color: '#fff', fontWeight: '700', fontSize: '13px', overflow: 'hidden',
                }}>
                  {avatarSrc
                    ? <img src={avatarSrc} alt={conv.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : isGroup ? <Users size={17} /> : <User size={17} />}
                </div>
                <span style={{ flex: 1, minWidth: 0, fontWeight: '600', fontSize: '14px', color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {conv.name}
                </span>
                <span style={{
                  width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                  border: picked ? 'none' : `2px solid ${c.border}`,
                  background: picked ? '#2e7cba' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                }}>
                  {picked && <Check size={14} />}
                </span>
              </div>
            );
          })}
          {candidates.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: c.textSecondary, fontSize: '14px' }}>
              {t.noResults}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${c.border}` }}>
          {selected.length > 0 && (
            <span style={{ fontSize: '13px', color: c.textSecondary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selected.map((x) => x.name).join('، ')}
            </span>
          )}
          <button
            type="button"
            className="btn-gradient"
            disabled={!selected.length || sending}
            onClick={handleSend}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 18px', borderRadius: '14px', border: 'none',
              cursor: selected.length ? 'pointer' : 'not-allowed',
              opacity: selected.length ? 1 : 0.5, fontWeight: '600',
            }}
          >
            <Send size={16} style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
            {t.send}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ForwardModal;
